import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronDown, MessageCircle, CheckCircle, Phone } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aurobindo-kids/artifacts/z058rndb_Aurobindo_Logo_WhiteBackgnd.png';
const HERO_IMAGE = 'https://images.unsplash.com/photo-1763310225230-6e15b125935a?w=1400&q=90';
const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20booking%20a%20school%20tour%20at%20Sri%20Aurobindo%20Montessori%20School';

const programs = [
  {
    tag: 'Toddler',
    age: '2 – 2.5 years',
    desc: 'A gentle first step away from family. Children settle in, find their feet, and fall in love with spoken language.',
    link: '/programs',
    anchor: '#toddler',
  },
  {
    tag: 'Primary',
    age: '2.5 – 6 years',
    desc: 'The years of the absorbent mind. Practical life, sensorial work, language, mathematics and culture — the whole world, offered to the child by six.',
    link: '/programs',
    anchor: '#primary',
  },
  {
    tag: 'Elementary',
    age: '6 – 12 years',
    desc: 'From "what" to "why". Small groups of 4–5 children explore the universe, work together, and move from materials into the world of ideas.',
    link: '/programs',
    anchor: '#elementary',
  },
];

const whyPoints = [
  {
    title: 'The child follows their interest — the teacher follows the child.',
    desc: 'In a traditional classroom, 25+ children follow one teacher and one blackboard. Here, learning is one-to-one until age six.',
  },
  {
    title: 'No tests, no homework, no comparison in the early years.',
    desc: 'Mastery comes through freely chosen repetition, not fear of marks. Confidence grows before the idea of "stage fear" can ever form.',
  },
  {
    title: 'Concrete before abstract.',
    desc: 'Children learn numbers by holding beads, geography by tracing real maps, grammar through movement — using every sense, not just the ears.',
  },
  {
    title: 'Mixed ages, real community.',
    desc: 'Older children naturally teach younger ones. Leadership isn\'t a certificate here; it\'s a daily habit.',
  },
  {
    title: 'Independence is the goal.',
    desc: 'Parents tell us the same thing year after year: "My child manages herself — even in the holidays."',
  },
];

const stats = [
  { number: '25+', label: 'Years in West Marredpally', sub: 'Est. 2000' },
  { number: '1,200+', label: 'Children guided', sub: 'Since founding' },
  { number: '7 of 10', label: 'Guides are AMI-trained', sub: 'Assoc. Montessori Internationale' },
  { number: '1 : 15', label: 'Adult-to-child ratio', sub: '2 AMI guides per environment' },
  { number: '0', label: 'Advertisements, ever', sub: 'Every family came through a family' },
];

const testimonials = [
  {
    quote: 'The difference in her focus and patience is remarkable compared to my other daughters at the same age in a CBSE setup. She sits with an activity and stays with it until she\'s done.',
    name: 'Ms. Megha',
    role: 'Mother of Dwisha · Primary',
    initials: 'M',
  },
  {
    quote: 'Our children wake up excited every morning because they genuinely love going to school — a feeling every parent hopes for.',
    name: 'Mr. Ashish Samat',
    role: 'Father of Anvi & Anika · Primary',
    initials: 'A',
  },
  {
    quote: 'He folds his clothes, eats on his own, arranges the table for everyone — and his knowledge of planets, continents and oceans is incredible for his age.',
    name: 'Ms. Payal',
    role: 'Mother of Mohak · Primary',
    initials: 'P',
  },
];

export default function Home() {
  return (
    <div>
      <Helmet>
        <title>Sri Aurobindo Montessori House of Children School – Secunderabad</title>
        <meta name="description" content="An authentic AMI Montessori school in West Marredpally, Secunderabad since 2000. Toddler, Primary &amp; Elementary programs for ages 2yrs–12yrs. Established 25+ years, 1200+ children groomed, grown purely by parents' word of mouth. Visit us." />
      </Helmet>

      {/* ── HERO ── */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center overflow-hidden"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A8A]/30 via-[#1E3A8A]/50 to-[#1E3A8A]/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center text-white">
          <img src={LOGO_URL} alt="School Logo" className="h-20 w-20 mx-auto mb-6 rounded-2xl shadow-2xl fade-in-up stagger-1" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5 fade-in-up stagger-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            A school where children learn<br className="hidden sm:block" /> because they want to.
          </h1>
          <p className="text-base sm:text-lg text-white/85 mb-2 font-medium fade-in-up stagger-3">
            Sri Aurobindo Montessori House of Children — an authentic Montessori environment in West Marredpally, Secunderabad, for children aged 2 to 12.&nbsp;&nbsp;Since 2000.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 fade-in-up stagger-4">
            <Link
              to="/admissions#enquiry-form"
              data-testid="hero-enquire-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-base hover:bg-blue-50 transition-all duration-200 hover:-translate-y-1 shadow-lg inline-flex items-center justify-center gap-2"
            >
              Book a School Tour <ArrowRight size={18} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-base hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-1 shadow-lg inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={20} /> WhatsApp Us
            </a>
          </div>
          <p className="mt-6 text-white/90 text-sm italic font-semibold leading-relaxed max-w-xl mx-auto fade-in-up stagger-5">
            No rows of benches. No blackboard-to-notebook copying. No fear. Just children choosing meaningful work — and growing independent, confident and kind.
          </p>
        </div>
        <a href="#welcome" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-white/80 transition-colors animate-bounce" aria-label="Scroll down">
          <ChevronDown size={28} />
        </a>
      </section>

      {/* ── WELCOME ── */}
      <section id="welcome" className="py-20 bg-white" data-testid="welcome-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Our School</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-8" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Welcome to the House of Children
          </h2>
          <div className="space-y-5 text-slate-600 leading-relaxed text-base">
            <p>
              Maria Montessori called her first school <em>Casa dei Bambini</em> — the House of Children. That is exactly what we have built here for over 25 years: a home away from home where the child, not the timetable, comes first.
            </p>
            <p>
              Here, a two-and-a-half-year-old pours her own water. A four-year-old writes because his hands were quietly prepared for it since he was two. A seven-year-old explains fractions to a friend using golden beads she can hold in her hands. Nobody is rushing them. Everybody is watching over them.
            </p>
          </div>
          {/* Philosophy callout */}
          <div className="mt-10 bg-[#EFF6FF] rounded-3xl px-8 py-7 border-l-4 border-[#1E3A8A]">
            <p className="text-[#1E3A8A] font-black text-xl sm:text-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Hands to work.&nbsp;&nbsp;Mind to think.&nbsp;&nbsp;Heart to love.
            </p>
            <p className="text-slate-500 text-sm mt-2">That is our whole philosophy in one line.</p>
          </div>
          <div className="mt-8">
            <Link to="/montessori" data-testid="welcome-discover-link" className="inline-flex items-center gap-2 text-[#1E3A8A] font-bold text-sm hover:underline">
              Discover the Montessori way <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── PROGRAMS ── */}
      <section className="py-20 bg-[#EFF6FF]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Ages 2 – 12</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Three programs, one unbroken journey
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programs.map((p, i) => (
              <div
                key={i}
                data-testid={`program-card-${i}`}
                className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(59,130,246,0.08)] hover:shadow-[0_14px_40px_rgba(59,130,246,0.14)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <span className="inline-block bg-[#DBEAFE] text-[#1E3A8A] text-xs font-bold rounded-full px-3 py-1 mb-4 self-start">{p.age}</span>
                <h3 className="text-xl font-extrabold text-[#0F172A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.tag}</h3>
                <p className="text-slate-500 text-sm leading-relaxed flex-1">{p.desc}</p>
                <Link
                  to={p.link}
                  className="mt-6 inline-flex items-center gap-1.5 text-[#1E3A8A] text-sm font-bold hover:underline"
                >
                  Explore {p.tag} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY MONTESSORI ── */}
      <section className="py-20 bg-white" data-testid="why-montessori-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">The Honest Difference</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Why parents choose Montessori<br className="hidden sm:block" /> over the usual route
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If you're comparing us with conventional CBSE, ICSE or State Board schools, here is the honest difference, in brief:
            </p>
          </div>
          <div className="space-y-4">
            {whyPoints.map((pt, i) => (
              <div key={i} data-testid={`why-point-${i}`} className="flex items-start gap-4 bg-[#F4F7FB] rounded-2xl px-6 py-5">
                <div className="w-7 h-7 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle size={15} className="text-[#1E3A8A]" />
                </div>
                <div>
                  <p className="font-bold text-[#0F172A] text-sm mb-1">{pt.title}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/montessori" data-testid="why-full-comparison-link" className="inline-flex items-center gap-2 text-[#1E3A8A] font-bold text-sm hover:underline">
              See the full Montessori vs. traditional comparison <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-[#1E3A8A] py-16" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-3">By the Numbers</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Numbers we've never had to advertise
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {stats.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center bg-white/10 rounded-2xl px-4 py-6">
                <p className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>{s.number}</p>
                <p className="text-blue-200 text-xs font-semibold mt-2 leading-snug">{s.label}</p>
                <p className="text-blue-400 text-xs mt-1 leading-snug">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Hook callout */}
          <div className="mt-10 max-w-xl mx-auto text-center bg-white/10 rounded-2xl px-8 py-6 border border-white/20" data-testid="stats-hook">
            <p className="text-white font-bold text-base mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Curious — why only 1,200 children in 25 years?
            </p>
            <Link to="/our-promise" className="inline-flex items-center gap-1.5 text-blue-200 hover:text-white text-sm font-semibold transition-colors">
              Read the choice we made <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Parents Speak</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>What parents tell us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-card-${i}`} className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(59,130,246,0.07)] hover:shadow-[0_14px_40px_rgba(59,130,246,0.12)] hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <p className="text-4xl text-[#BFDBFE] font-black leading-none mb-4">"</p>
                <p className="text-slate-700 text-sm leading-relaxed italic flex-1">{t.quote}</p>
                <div className="flex items-center gap-3 pt-5 mt-5 border-t border-blue-50">
                  <div className="w-9 h-9 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#1E3A8A] font-black text-sm">{t.initials}</span>
                  </div>
                  <div>
                    <p className="font-bold text-[#0F172A] text-sm">{t.name}</p>
                    <p className="text-slate-400 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/testimonials" data-testid="testimonials-read-more-link" className="inline-flex items-center gap-2 text-[#1E3A8A] font-bold text-sm hover:underline">
              Read more parent experiences <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 bg-white" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Visit Us</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Come and see it<br className="hidden sm:block" /> for yourself
          </h2>
          <p className="text-slate-600 leading-relaxed text-base mb-10">
            Words can only explain Montessori. A visit lets you feel it — the calm, the concentration, the happy hum of children at work. Walk through our environments, ask us anything, and take your time deciding. We will never pressure you; we'll simply show you what your child's mornings could look like.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="cta-book-tour-btn"
              className="bg-[#1E3A8A] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a School Tour <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              className="border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>WhatsApp</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
            <a
              href="tel:9246887272"
              data-testid="cta-call-btn"
              className="border-2 border-slate-200 text-slate-600 rounded-full px-8 py-4 font-black text-sm hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <Phone size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>Call</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
          </div>
          <p className="mt-5 text-slate-400 text-xs">
            Visits by appointment, Monday – Friday &nbsp;·&nbsp; West Marredpally, Secunderabad
          </p>
        </div>
      </section>

    </div>
  );
}
